
























































































import {
    defineComponent,
    onMounted,
    provide,
    Ref,
    ref,
    nextTick,
    watch,
    PropType,
    computed,
    useContext,
} from "@nuxtjs/composition-api";
import {
    SfAccordion,
    SfButton,
    SfFilter,
    SfHeading,
    SfRadio,
    SfSidebar,
} from "@storefront-ui/vue";

import { clearAllBodyScrollLocks } from "body-scroll-lock";
import SkeletonLoader from "components/theme/SkeletonLoader.vue";
import { useUiHelpers } from "@wemade-vsf/composables";
import { getFilterConfig } from "@wemade-vsf/catalog/lib/runtime/category/config/FiltersConfig";
import SelectedFilters from "components/catalog/Category/Filters/SelectedFilters.vue";
import { useTraverseCategory } from "@wemade-vsf/catalog/lib/runtime/category/helpers/useTraverseCategory";
import type { Aggregation, AggregationOption } from "@wemade-vsf/magento-api";
import type { SelectedFiltersInterface } from "@wemade-vsf/catalog/lib/runtime/category/composables/useFilters";
import { useFilters } from "@wemade-vsf/catalog/lib/runtime/category/composables/useFilters";
import { usePartFinderStore } from "~/modules/part-finder/stores/partFinder";
import { storeToRefs } from "pinia";

export interface UseFiltersProviderInterface {
    selectedFilters: Ref<SelectedFiltersInterface>;
    filters: Ref<Aggregation[]>;
}

export default defineComponent({
    name: "CategoryFilters",
    components: {
        SelectedFilters,
        SkeletonLoader,
        CategoryType: () =>
            import(
                "components/catalog/Category/Filters/Renderer/CategoryType.vue"
            ),
        CheckboxType: () =>
            import(
                "components/catalog/Category/Filters/Renderer/CheckboxType.vue"
            ),
        SwatchType: () =>
            import(
                "components/catalog/Category/Filters/Renderer/SwatchType.vue"
            ),
        RadioType: () =>
            import(
                "components/catalog/Category/Filters/Renderer/RadioType.vue"
            ),
        YesNoType: () =>
            import(
                "components/catalog/Category/Filters/Renderer/YesNoType.vue"
            ),
        RangeType: () =>
            import(
                "components/catalog/Category/Filters/Renderer/RangeType.vue"
            ),
        PartFinderFilter: () =>
            import("~/modules/part-finder/components/PartFinderFilter.vue"),
        SfSidebar,
        SfHeading,
        SfAccordion,
        SfFilter,
        SfButton,
        SfRadio,
    },
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
        isCategory: {
            type: Boolean,
            default: true,
        },
        catUid: {
            type: String,
            default: "",
        },
        aggregations: {
            type: Array as PropType<Aggregation[]>,
            default: [],
        },
        useRootCategories: {
            type: Boolean,
            default: false,
        },
        showPartFinder: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const { app } = useContext();
        const { changeFilters, clearFilters } = useUiHelpers();
        const filters = ref<Aggregation[]>([]);
        const isLoading = ref(true);

        const store = usePartFinderStore();
        const { brands, models, finders } = storeToRefs(store);

        const removableFilters = computed(() => [
            ...nativeRemovableFilters.value,
            ...getSelectedPartFinderFilters(),
        ]);

        const {
            selectedFilters,
            removableFilters: nativeRemovableFilters,
            selectFilter,
            removeFilter,
            isFilterSelected,
            getRemovableFilters,
            clearSelectedFilters,
        } = useFilters();

        function getSelectedPartFinderFilters() {
            let pfFilters = [];
            if (selectedFilters.value["pf_brand"]?.length) {
                const slug = Array.isArray(selectedFilters.value.pf_brand)
                    ? selectedFilters.value.pf_brand[0]
                    : selectedFilters.value.pf_brand;
                const brand = brands.value.find((b) => b.slug === slug);
                pfFilters.push({
                    id: "pf_brand",
                    name: app.i18n.t("part-finder.brand"),
                    label: brand ? brand.name : slug,
                    value: slug,
                    type: "RadioType",
                });
            }
            if (selectedFilters.value["pf_model"]?.length) {
                const slug = Array.isArray(selectedFilters.value.pf_model)
                    ? selectedFilters.value.pf_model[0]
                    : selectedFilters.value.pf_model;
                const model = models.value.find((m) => m.slug === slug);
                pfFilters.push({
                    id: "pf_model",
                    name: app.i18n.t("part-finder.model"),
                    label: model ? model.name : slug,
                    value: slug,
                    type: "RadioType",
                });
            }
            if (selectedFilters.value["pf_finder"]?.length) {
                const slug = Array.isArray(selectedFilters.value.pf_finder)
                    ? selectedFilters.value.pf_finder[0]
                    : selectedFilters.value.pf_finder;
                const finder = finders.value.find((t) => t.slug === slug);
                pfFilters.push({
                    id: "pf_finder",
                    name: app.i18n.t("part-finder.finder"),
                    label: finder ? finder.name : slug,
                    value: slug,
                    type: "RadioType",
                });
            }
            return pfFilters;
        }

        const updateRemovableFilters = () => {
            getRemovableFilters(filters.value, selectedFilters.value);
        };

        const {
            isCategoryTreeLoaded,
            loadCategoryTree,
            activeCategory,
            categoryTree,
        } = useTraverseCategory();

        const doApplyFilters = () => {
            changeFilters(selectedFilters.value, false);
            updateRemovableFilters();
            if (window?.scroll) {
                window.scroll(0, 0);
            }
            emit("reloadProducts");
            emit("close");
        };

        const doRemoveFilter = ({
            id,
            value,
        }: {
            id: string;
            value: string;
        }) => {
            removeFilter(id, value);
            changeFilters(selectedFilters.value, false);
            updateRemovableFilters();
            emit("reloadProducts");
            emit("close");
        };

        const doClearFilters = () => {
            clearFilters(false);
            clearSelectedFilters();
            updateRemovableFilters();
            emit("reloadProducts");
            emit("close");
        };

        const filterChanged = (
            filter: Aggregation,
            option: AggregationOption
        ) => {
            selectFilter(filter, option);
            doApplyFilters();
        };

        function applyPartfinder(options: {
            brand: string;
            model: string;
            year?: number;
        }) {
            if (!options.brand || !options.model) return;
            selectFilter(
                { attribute_code: "pf_brand" },
                { value: options.brand },
                false
            );
            selectFilter(
                { attribute_code: "pf_model" },
                { value: options.model },
                false
            );
            if (options.year) {
                selectFilter(
                    { attribute_code: "pf_years" },
                    { value: String(options.year) },
                    false
                );
            }
            doApplyFilters();
        }

        function removePartfinder(options) {
            removeFilter("pf_brand", options.brand);
            removeFilter("pf_model", options.model);
            if (options.year) {
                removeFilter("pf_years", String(options.year));
            }
            doApplyFilters();
        }

        watch(
            () => props.isVisible,
            (newValue) => {
                // disable Storefrontt UI's body scroll lock which is launched when :visible prop on SfSidebar changes
                // two next ticks because SfSidebar uses nextTick aswell, and we want to do something after that tick.
                if (newValue) {
                    nextTick(() => nextTick(() => clearAllBodyScrollLocks()));
                }
            }
        );
        watch(
            () => props.aggregations,
            (newValue) => {
                filters.value = newValue.filter(
                    (f) =>
                        f.attribute_code !== "category_id" &&
                        f.attribute_code !== "category_uid"
                );
            },
            { immediate: true, deep: true }
        );

        onMounted(async () => {
            filters.value = props.aggregations.filter(
                (f) =>
                    f.attribute_code !== "category_uid" &&
                    f.attribute_code !== "category_id"
            );

            if (
                (props.isCategory || props.useRootCategories) &&
                !isCategoryTreeLoaded.value
            ) {
                await loadCategoryTree();
            }
            updateRemovableFilters();
            isLoading.value = false;
        });

        provide("UseFiltersProvider", {
            isFilterSelected,
            selectedFilters,
            filters,
        });

        return {
            selectFilter,
            filterChanged,
            doApplyFilters,
            doRemoveFilter,
            doClearFilters,
            getFilterConfig,
            applyPartfinder,
            removePartfinder,
            selectedFilters,
            filters,
            isLoading,
            removableFilters,
            activeCategory,
        };
    },
});
